import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Clock, Sparkles, Shield, UserCheck, AlertTriangle, ArrowRight, Share2, BookOpen } from 'lucide-react';
import { processYouTubeEmbeds } from '../lib/utils';
import { trackBlogPostView } from '../lib/analytics';
import Navigation from './Navigation';
import Footer from './Footer';
import SEOHead from './SEOHead';

const FALLBACK_POSTS = {
  '5-essential-steps-to-build-longterm-wealth': {
    title: '5 Essential Steps to Build Long-Term Wealth',
    metaDescription: 'Learn the 5 essential steps to build long-term wealth, protect your assets, and achieve financial independence.',
    content: `
      <h2>1. Establish a Guaranteed Financial Foundation</h2>
      <p>Building long-term wealth begins by protecting your downside. Without guaranteed market floors, years of compound growth can be wiped out in a single market correction. Utilizing IRC Sec. 7702 contractual structures ensures a 0% market downside floor while capturing upward market participation.</p>
      
      <h2>2. Optimize Tax-Free Capital Accumulation</h2>
      <p>Taxes are often the single largest drag on long-term compound wealth. Structuring cash values inside tax-advantaged vehicles allows your money to grow 100% tax-free, with tax-free access to liquidity throughout your retirement years.</p>
      
      <h2>3. Secure Comprehensive Living Benefits</h2>
      <p>Traditional financial plans fail to account for health emergencies. Living Benefits protection allows policyholders to access acceleration of death benefits while living during critical, chronic, or terminal health conditions, safeguarding family savings.</p>

      <h2>4. Eliminate Hidden Fee Drag</h2>
      <p>High management fees silently erode capital. Working with state-licensed financial professionals to audit fee structures ensures maximum capital retention over a 20-30 year wealth building horizon.</p>

      <h2>5. Create a Resilient Estate Legacy Plan</h2>
      <p>Wealth preservation is about generational continuity. Designing tax-exempt death benefits guarantees seamless estate transfers to heirs without probate delays or tax liabilities.</p>
    `,
    publishedDate: '2026-01-15',
    author: 'Priyanka Karn'
  },
  'why-i-built-grow-and-stay-rich-stories-that-taught-me-the-real-meaning-of-wealth': {
    title: 'Why I Built Grow And Stay Rich: Stories That Taught Me the Real Meaning of Wealth',
    metaDescription: 'Personal stories and real-world experiences that inspired the creation of Grow And Stay Rich.',
    content: `
      <h2>The Genesis of Grow And Stay Rich</h2>
      <p>Over the course of my financial advisory career, I repeatedly witnessed hard-working families lose substantial portions of their savings to market downturns, unexpected health crises, or aggressive tax brackets. These experiences revealed a fundamental gap in traditional financial planning.</p>
      
      <h2>True Wealth is About Protection & Freedom</h2>
      <p>Real wealth isn't just about accumulating numbers on a screen — it is about achieving absolute peace of mind. By combining tax-advantaged capital growth with Living Benefits and fiduciary advisory standards, we empower families to build legacies that endure.</p>

      <h2>Our Fiduciary Commitment</h2>
      <p>Grow And Stay Rich was established with a singular mission: to provide transparent, educational, and high-impact wealth architecture to business leaders and families across the United States.</p>
    `,
    publishedDate: '2026-01-20',
    author: 'Priyanka Karn'
  },
  'money-truths-to-build-wealth': {
    title: 'Conquer Your Financial Freedom: Fundamental Money Truths',
    metaDescription: 'Uncover fundamental money truths and wealth-building principles to take control of your financial destiny.',
    content: `
      <h2>Truth #1: Market Volatility Can Be Optional</h2>
      <p>Most investors assume high market risk is mandatory for competitive returns. Fixed Index Annuities and Index Universal Life strategies disprove this myth by offering upside index participation with a contractual 0% downside floor.</p>

      <h2>Truth #2: Tax Efficiency Outperforms Pure Return</h2>
      <p>Earning 8% tax-free can yield higher net retirement income than earning 10% taxable. Understanding IRC Sec. 7702 tax rules is essential for high earners seeking lifetime tax-free distributions.</p>

      <h2>Truth #3: Health Asset Protection is Non-Negotiable</h2>
      <p>Medical emergencies represent a leading cause of unexpected asset liquidation. Integrating Living Benefits into your financial plan insulates your retirement nest egg from healthcare shocks.</p>
    `,
    publishedDate: '2026-02-01',
    author: 'Priyanka Karn'
  }
};

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blogPost, setBlogPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlogPost = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(`${process.env.REACT_APP_API_BASE || '/api'}/blog/${slug}`);
        const contentType = response.headers.get('content-type');
        
        if (!response.ok || !contentType || !contentType.includes('application/json')) {
          if (FALLBACK_POSTS[slug]) {
            setBlogPost(FALLBACK_POSTS[slug]);
            setError(null);
            setLoading(false);
            return;
          }
          throw new Error('Failed to fetch blog post');
        }
        
        const data = await response.json();
        setBlogPost(data.data);
        
        // Track blog post view
        if (data.data) {
          trackBlogPostView(data.data.title || slug, slug);
        }
      } catch (err) {
        console.error('Error fetching blog post:', err);
        if (FALLBACK_POSTS[slug]) {
          setBlogPost(FALLBACK_POSTS[slug]);
          setError(null);
        } else {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchBlogPost();
    }
  }, [slug]);

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'Available';
    }
  };

  const goBack = () => {
    navigate('/blog');
  };

  // Process blog post content to convert YouTube URLs to embeds
  const processedContent = useMemo(() => {
    if (!blogPost?.content) return '';
    return processYouTubeEmbeds(blogPost.content);
  }, [blogPost?.content]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#070E1B] text-white font-opensans flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C9A84C] mx-auto" />
          <p className="text-slate-300 text-sm">Loading financial article...</p>
        </div>
      </div>
    );
  }

  if (error || !blogPost) {
    return (
      <div className="min-h-screen bg-[#070E1B] text-white font-opensans flex flex-col justify-between">
        <Navigation />
        <div className="flex-1 flex items-center justify-center py-20 px-4">
          <div className="text-center max-w-md mx-auto bg-[#162236]/90 border border-[#C9A84C]/30 p-8 rounded-2xl shadow-2xl backdrop-blur-xl space-y-4">
            <BookOpen className="h-12 w-12 text-[#C9A84C] mx-auto opacity-70" />
            <h1 className="text-2xl font-bold font-playfair text-white">Article Not Found</h1>
            <p className="text-sm text-slate-300">{error || "The requested blog article is currently unavailable."}</p>
            <button
              onClick={goBack}
              className="inline-flex items-center px-6 py-3 bg-gradient-gold-cta text-[#070E1B] rounded-xl font-bold text-xs shadow-lg hover:opacity-95 transition-all"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Return to Knowledge Hub
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Build canonical URL and image URL
  const canonicalUrl = blogPost.link || (typeof window !== 'undefined' ? `${window.location.origin}/blog/${slug}` : `https://growandstayrich.com/blog/${slug}`);
  const ogImage = blogPost.image?.startsWith('http') 
    ? blogPost.image 
    : (typeof window !== 'undefined' ? `${window.location.origin}${blogPost.image || '/images/og-priyanka-office.jpg'}` : `https://growandstayrich.com/images/og-priyanka-office.jpg`);

  return (
    <div className="min-h-screen bg-[#070E1B] text-white font-opensans flex flex-col justify-between">
      <SEOHead 
        title={`${blogPost.title} | Grow & Stay Rich`}
        description={blogPost.metaDescription || blogPost.title}
        canonicalUrl={canonicalUrl}
        ogImage={ogImage}
        ogType="article"
        path={`/blog/${slug}`}
      />

      <div>
        <Navigation />

        {/* Article Header Banner */}
        <div className="relative py-20 pt-28 bg-[#070E1B] text-white border-b border-[#C9A84C]/25 overflow-hidden">
          {/* Ambient Lighting */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
            <div className="absolute -top-32 left-1/3 w-[500px] h-[500px] bg-[#C9A84C]/15 rounded-full blur-[140px] animate-pulse-glow" />
            <div className="absolute top-1/3 -right-32 w-[600px] h-[600px] bg-[#3D6ACC]/20 rounded-full blur-[160px]" />
          </div>

          <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <button
              onClick={goBack}
              className="inline-flex items-center text-xs font-semibold text-[#E8D4A0] hover:text-white bg-[#162236] border border-[#C9A84C]/30 px-3.5 py-1.5 rounded-full mb-6 transition-all group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]"
            >
              <ArrowLeft className="h-3.5 w-3.5 mr-2 group-hover:-translate-x-1 transition-transform text-[#C9A84C]" />
              Back to Articles
            </button>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold font-playfair tracking-tight leading-tight">
                {blogPost.title}
              </h1>

              {/* Author & Date Metadata Row */}
              <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-slate-800 text-xs text-slate-300">
                <div className="flex items-center space-x-6">
                  {/* Author Avatar Badge */}
                  <div className="flex items-center space-x-2.5 bg-[#162236] border border-[#C9A84C]/30 px-3 py-1.5 rounded-full">
                    <img
                      src="/images/priyanka-office.jpg"
                      alt="Priyanka Karn"
                      className="w-6 h-6 rounded-full border border-[#C9A84C] object-cover"
                    />
                    <span className="font-semibold text-white">Priyanka Karn</span>
                    <span className="text-[10px] text-[#E8D4A0] font-bold uppercase">• Fiduciary Specialist</span>
                  </div>

                  <div className="flex items-center gap-1.5 text-slate-300">
                    <Calendar className="h-4 w-4 text-[#C9A84C]" />
                    <span>{formatDate(blogPost.publishedDate)}</span>
                  </div>
                  
                  <div className="flex items-center gap-1.5 text-slate-300">
                    <Clock className="h-4 w-4 text-[#C9A84C]" />
                    <span>5 min read</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 bg-[#162236] border border-[#C9A84C]/30 text-[#E8D4A0] text-[11px] font-bold rounded-full uppercase">
                    Financial Education
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Article Body Content Container */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-12"
        >
          {/* Main Article Body (Dark Mode Typography) */}
          <div className="prose prose-lg prose-invert max-w-none font-opensans prose-headings:font-playfair prose-headings:text-white prose-p:text-slate-300 prose-p:leading-relaxed prose-a:text-[#E8D4A0] hover:prose-a:text-white prose-strong:text-white prose-blockquote:border-l-[#C9A84C] prose-blockquote:bg-[#162236]/80 prose-blockquote:text-slate-200 prose-ul:text-slate-300 prose-li:text-slate-300">
            <div dangerouslySetInnerHTML={{ __html: processedContent }} />
          </div>

          {/* Prominent Lead Magnet / Consultation CTA Banner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="p-8 sm:p-10 bg-[#162236]/90 border border-[#C9A84C]/40 rounded-2xl shadow-2xl backdrop-blur-xl relative overflow-hidden text-center space-y-6"
          >
            <div className="absolute top-0 right-0 w-48 h-48 bg-[#C9A84C]/10 rounded-full blur-3xl pointer-events-none" />
            
            <div className="space-y-3 relative z-10">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#0D1B2A] border border-[#C9A84C]/30 text-xs font-semibold text-[#E8D4A0]">
                <Sparkles className="w-4 h-4 text-[#C9A84C]" />
                Custom Financial Strategy Consultation
              </div>
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white font-playfair">
                Ready to Architect Your Tax-Free Future?
              </h3>
              <p className="text-sm sm:text-base text-slate-300 max-w-2xl mx-auto leading-relaxed">
                Schedule a confidential 1-on-1 strategy session with our licensed professionals to build your custom Financial Needs Analysis (FNA).
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center relative z-10">
              <button
                onClick={() => navigate('/#booking')}
                className="px-8 py-4 bg-gradient-gold-cta text-[#070E1B] rounded-xl font-bold text-base shadow-xl hover:opacity-95 transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]"
              >
                Schedule Free Consultation
              </button>
              <button
                onClick={() => navigate('/#lead-magnet')}
                className="px-8 py-4 border border-[#C9A84C]/50 text-[#E8D4A0] rounded-xl font-semibold hover:bg-[#C9A84C]/10 transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]"
              >
                Download Free Wealth Blueprint
              </button>
            </div>

            <div className="pt-2 text-xs text-slate-400 relative z-10">
              <span>No Obligation • 100% Confidential • Licensed Advisory</span>
            </div>
          </motion.div>

          {/* Legal & Regulatory Disclaimer Card */}
          <div className="p-6 sm:p-8 bg-[#0D1B2A]/90 border border-[#C9A84C]/30 rounded-2xl shadow-xl space-y-4 text-xs text-slate-300 leading-relaxed">
            <div className="flex items-center gap-2 text-[#E8D4A0] font-bold uppercase tracking-wider text-xs">
              <AlertTriangle className="h-4 w-4 text-[#C9A84C]" />
              Important Financial Disclaimer
            </div>
            <div className="space-y-2 text-slate-400">
              <p>
                <strong>Educational Information Only:</strong> The information provided in this article is for general educational purposes only and does not constitute personalized financial, tax, investment, legal, or insurance advice.
              </p>
              <p>
                <strong>Professional Advisory Required:</strong> Financial strategies, tax-advantaged growth vehicles (IRC Sec. 7702), and contractual guarantees (such as 0% downside protection) depend on carrier underwriting approval, contract terms, and state regulations. Always consult with state-licensed insurance and financial professionals before making policy decisions.
              </p>
            </div>
          </div>

          {/* Author Bio Card */}
          <div className="p-8 bg-[#162236]/90 border border-[#C9A84C]/30 rounded-2xl shadow-xl backdrop-blur-xl">
            <div className="flex flex-col sm:flex-row gap-6 items-center sm:items-start">
              <img
                src="/images/priyanka-office.jpg"
                alt="Priyanka Karn"
                className="w-28 h-28 rounded-full border-2 border-[#C9A84C] object-cover shadow-xl shrink-0"
              />
              
              <div className="space-y-3 text-center sm:text-left">
                <div>
                  <h3 className="text-xl font-bold font-playfair text-white">About the Author</h3>
                  <h4 className="text-sm font-semibold text-[#E8D4A0]">Priyanka Karn — Lead Wealth Strategist & Founder</h4>
                </div>
                <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                  Priyanka Karn is a state-licensed life and financial professional dedicated to empowering families and business leaders with tax-free wealth accumulation, Living Benefits protection, and zero-downside market retirement strategies across the United States.
                </p>
                <div className="pt-2 border-t border-slate-700/60 flex flex-wrap items-center justify-center sm:justify-start gap-4 text-xs text-slate-300">
                  <div className="flex items-center gap-1.5">
                    <Shield className="w-4 h-4 text-[#C9A84C]" />
                    <span>State Licensed Specialist</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <UserCheck className="w-4 h-4 text-[#C9A84C]" />
                    <span>Fiduciary Standard Advisory</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </motion.div>
      </div>

      <Footer />
    </div>
  );
}
